// GAPI-43148 — artwork signals used by the host (dashboard) to recommend DTF vs DTG.
// The editor only computes RAW signals; the recommendation decision lives in the host.

export const PRINT_TECH_ANALYSIS_VERSION = 1;

/** Structural raster input — kept DOM-free so the pure analyzer is unit-testable
 *  without a canvas (jsdom does not implement getImageData). Matches the shape of
 *  the browser `ImageData`. */
export interface RasterPixels {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

/** Raw pixel-level statistics for a single rasterized image. Kept as counts (not
 *  ratios) so they can be aggregated across several image elements on the same
 *  print area before ratios are derived. */
export interface PrintTechPixelStats {
  /** Total pixels read from the raster. */
  sampledPixels: number;
  /** Pixels with alpha above the "visible" threshold. */
  visiblePixels: number;
  /** Pixels with alpha at/above the "opaque" threshold (denominator for colour/edge ratios). */
  opaquePixels: number;
  /** Pixels with alpha at/above the "strong opaque" threshold (used for coverage). */
  strongOpaquePixels: number;
  /** Pixels whose alpha sits between visible and strong-opaque — soft/faded edges. */
  semiTransparentPixels: number;
  /** Opaque pixels whose Sobel luminance-gradient magnitude is small-but-nonzero — smooth transitions. */
  smoothGradientPixels: number;
  /** Opaque pixels whose Sobel luminance-gradient magnitude is strong — fine lines / crisp edges. */
  strongEdgePixels: number;
  /** Posterized (12-bit) colour key -> opaque pixel count. */
  colorBucketCounts: Record<number, number>;
}

/** Final per-print-area signals sent to the host. */
export interface PrintTechSignals {
  /** Distinct posterized colours holding a non-trivial share of the opaque pixels. */
  colorCount: number;
  /** Share of opaque pixels in smooth-gradient transitions (0..1). */
  gradientRatio: number;
  /** Share of opaque pixels on strong edges — fine detail (0..1). */
  edgeDensity: number;
  /** Estimated fraction of the print area covered by opaque artwork (0..1). */
  coverage: number;
  /** Share of visible pixels that are semi-transparent — DTF white-underbase halo risk (0..1). */
  semiTransparentRatio: number;
}

export interface PrintTechAreaAnalysis {
  spreadIndex: number;
  spreadName: string;
  /** Raw editor PrintTechnology value for the spread ('DTG' | 'DTF'). */
  technology: string;
  signals: PrintTechSignals;
}

export interface PrintTechAnalysis {
  analysisVersion: number;
  /** Stable hash of the analyzed design — lets the host remember dismissals per design. */
  designHash: string;
  areas: PrintTechAreaAnalysis[];
}
